import { Clock, Calendar as CalendarIcon } from 'lucide-react';
import { handleTimeAndDateTabOnChangeEventThunk } from '@common-reducers/CampaignThunks';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import moment from 'moment';
import { useMemo } from 'react';
import CampaignTabHeader from '../campaign-tab-header';
import BlueticksDatePicker from './bt-date-picker';
import BlueticksTimePicker from './bt-time-picker';
import CampaignMaxSendWindowControl from './campaign-max-send-window-control';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common-config/constants';

export default function BulkSetTimeAndDate() {
  const dispatch = useAppDispatch()
  const { t } = useTranslation(namespace);
  const newCampaign = useAppSelector(state => state.CampaignReducer.newCampaign)

  const switchCampaignScheduleTimingToNow = () => {
    const now = moment();
    const localDate = now.toISOString();

    dispatch(handleTimeAndDateTabOnChangeEventThunk({
      changeType: "setScheduleDueDateToNow",
      changeObject: localDate,
    }))
  }

  const handleTimingRadioButtonChange = (type: string) => {
    if (type === 'edit') {
      const existingDueDate = newCampaign?.schedule?.dueDate;
      const now = moment();

      let scheduledMoment = existingDueDate ? moment(existingDueDate) : null;

      if (!scheduledMoment || !scheduledMoment.isValid() || !scheduledMoment.isAfter(now)) {
        scheduledMoment = moment().add(5, 'minutes').seconds(0).milliseconds(0);
      }

      dispatch(handleTimeAndDateTabOnChangeEventThunk({
        changeType: "setScheduleDueDateToNotNow",
        changeObject: { dueDate: scheduledMoment.toISOString(), isNow: false },
      }))
    }
    if (type === 'now') {
      switchCampaignScheduleTimingToNow()
    }
  }

  // Convert ISO date to dd/mm/yyyy format
  const dateValue = useMemo(() => {
    if (!newCampaign?.schedule?.dueDate) return '';
    const m = moment(newCampaign.schedule.dueDate);
    if (!m.isValid()) return '';
    const day = String(m.date()).padStart(2, '0');
    const month = String(m.month() + 1).padStart(2, '0');
    const year = m.year();
    return `${day}/${month}/${year}`;
  }, [newCampaign?.schedule?.dueDate]);

  // Convert ISO date to HH:mm format
  const timeValue = useMemo(() => {
    if (!newCampaign?.schedule?.dueDate) return '';
    const m = moment(newCampaign.schedule.dueDate);
    if (!m.isValid()) return '';
    return m.format('HH:mm');
  }, [newCampaign?.schedule?.dueDate]);

  const handleDateChange = (dateString: string) => {
    // dateString is in dd/mm/yyyy format
    const parts = dateString.split('/');
    if (parts.length !== 3) return;
    
    const [day, month, year] = parts.map(Number);
    const currentMoment = newCampaign?.schedule?.dueDate 
      ? moment(newCampaign.schedule.dueDate)
      : moment();
    
    const newMoment = currentMoment
      .year(year)
      .month(month - 1)
      .date(day);

    if (!newMoment.isValid()) return;

    dispatch(handleTimeAndDateTabOnChangeEventThunk({
      changeType: "setScheduleDueDate",
      changeObject: newMoment.toISOString(),
    }));
  };

  const handleTimeChange = (timeString: string) => {
    // timeString is in HH:mm format
    const [hours, minutes] = timeString.split(':').map(Number);
    const currentMoment = newCampaign?.schedule?.dueDate 
      ? moment(newCampaign.schedule.dueDate)
      : moment();
    
    const newMoment = currentMoment
      .hour(hours)
      .minute(minutes)
      .second(0)
      .millisecond(0);

    if (!newMoment.isValid()) return;

    dispatch(handleTimeAndDateTabOnChangeEventThunk({
      changeType: "setScheduleDueDate",
      changeObject: newMoment.toISOString(),
    }));
  };

  const handleTimeAndDateTabOnChangeEvent = (changeType: string, changeObject: any) => {
    dispatch(handleTimeAndDateTabOnChangeEventThunk({ changeType, changeObject }))
  }

  return (
    <div className="flex flex-col items-center max-w-2xl mx-auto w-full px-6 pb-12">
      <CampaignTabHeader
        title={t('dateTime.whenToSend')}
        subTitle={t('campaign.launchCampaignNowOrSchedule')} />

      <div className="w-full space-y-4 mt-8">
        {/* Now Option */}
        <div 
          onClick={() => handleTimingRadioButtonChange('now')}
          className={cn(
            "bg-white dark:bg-slate-800 border-2 rounded-xl p-6 cursor-pointer transition-all",
            newCampaign?.schedule?.isNow 
              ? "border-sky-500 dark:border-sky-500 shadow-lg" 
              : "border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500"
          )}
        >
          <div className="flex items-center gap-4">
            <div className={cn(
              "w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0",
              newCampaign?.schedule?.isNow 
                ? "border-sky-500 dark:border-sky-500" 
                : "border-slate-300 dark:border-slate-600"
            )}>
              {newCampaign?.schedule?.isNow && (
                <div className="w-3 h-3 rounded-full bg-sky-500 dark:bg-sky-500"></div>
              )}
            </div>
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-slate-600 dark:text-slate-400" />
              <span className="text-lg font-semibold text-slate-900 dark:text-slate-50">{t('dateTime.now')}</span>
            </div>
          </div>
        </div>

        {/* Schedule Option */}
        <div 
          onClick={() => handleTimingRadioButtonChange('edit')}
          className={cn(
            "bg-white dark:bg-slate-800 border-2 rounded-xl p-6 cursor-pointer transition-all",
            !newCampaign?.schedule?.isNow 
              ? "border-sky-500 dark:border-sky-500 shadow-lg" 
              : "border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500"
          )}
        >
          <div className="flex items-center gap-4">
            <div className={cn(
              "w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0",
              !newCampaign?.schedule?.isNow 
                ? "border-sky-500 dark:border-sky-500" 
                : "border-slate-300 dark:border-slate-600"
            )}>
              {!newCampaign?.schedule?.isNow && (
                <div className="w-3 h-3 rounded-full bg-sky-500 dark:bg-sky-500"></div>
              )}
            </div>
            <div className="flex items-center gap-3">
              <CalendarIcon className="w-5 h-5 text-slate-600 dark:text-slate-400" />
              <span className="text-lg font-semibold text-slate-900 dark:text-slate-50">{t('dateTime.schedule')}</span>
            </div>
          </div>
          
          {!newCampaign?.schedule?.isNow && (
            <div className="flex gap-4 ml-10 mt-4" onClick={(e) => e.stopPropagation()}>
              <div className="flex-1">
                <BlueticksDatePicker 
                  value={dateValue} 
                  onChange={handleDateChange}
                  primaryColor="#259ae9"
                  lightColor="#e0f2fe"
                  darkColor="#0284c7"
                />
              </div>
              <div className="flex-1">
                <BlueticksTimePicker 
                  value={timeValue} 
                  onChange={handleTimeChange}
                  primaryColor="#259ae9"
                  lightColor="#e0f2fe"
                  darkColor="#0284c7"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="w-full mt-4">
        <CampaignMaxSendWindowControl handleTimeAndDateTabOnChangeEvent={handleTimeAndDateTabOnChangeEvent} />
      </div>
    </div>
  )
}

/*

DONT FORGET WE HAVE THESE COMPONENTS READT FOR ADVANCED TIME SETTINGS
  return (
              <CampaignTimingSpecificHoursDialog
            handleTimeAndDateTabOnChangeEvent={handleTimeAndDateTabOnChangeEvent}
          />
          <CampaignTimingAdvancedOptions
            handleTimeAndDateTabOnChangeEvent={handleTimeAndDateTabOnChangeEvent}
          />
  )
*/













// this is not yet supported  PLEASE DO NOT DELETE - THIS IS THE RRRULE FOR RECURING CAMPAIGNS


// this is not yet supported  PLEASE DO NOT DELETE
// const [anchorElPattern, setAnchorElPattern] = useState(null);
// const [selectedMonthlyRule, setSelectedMonthlyRule] = useState(0);
// const [ends, setEnds] = useState<"never" | "on" | "after">("never");
// const [endsDate, setEndsDate] = useState<moment.Moment>(moment());
// const [endsOccurrences, setEndsOccurrences] = useState<number>(0);
// const [ruleOptions, setRuleOptions] = useState<CherryPickOptions>(initialRuleOptions(moment()));


// const handlePatternClick = (e) => {
//   e.stopPropagation()
//   setAnchorElPattern(anchorElPattern ? null : e.currentTarget);
// };


// this is not yet supported  PLEASE DO NOT DELETE
// const handleIntervalChange = (event: React.ChangeEvent<any>) => {
//   setRuleOptions({
//     ...ruleOptions,
//     interval: event.target.value as number,
//   });
// };
// const handleFrequencyChange = (event: React.ChangeEvent<any>) => {
//   setRuleOptions({
//     ...ruleOptions,
//     freq: event.target.value as Frequency,
//   });
// };
// const handleByweekdayChange = (_, value: number[]) => {
//   setRuleOptions({
//     ...ruleOptions,
//     byweekday: value,
//   });
// };




// this is not yet supported  PLEASE DO NOT DELETE
// const lastValidTime = useRef<moment.Moment>();


// this is not yet supported  PLEASE DO NOT DELETE
// let dueDate = useMemo(() => {
//   if (dueTime) return dueTime;
//   else if (schedule?.dueDate && moment(schedule?.dueDate).isValid())
//     return moment(schedule?.dueDate);

//   return lastValidTime.current;
// }, [dueTime, schedule?.dueDate]);


// this is not yet supported  PLEASE DO NOT DELETE
// let monthlyRules = useMemo(() => {
//   if (ruleOptions.freq === Frequency.MONTHLY) {
//     const options = {
//       freq: ruleOptions.freq,
//       interval: ruleOptions.interval,
//     };

//     const bymonthdayRule = new RRule({
//       ...options,
//       bymonthday: dueDate.date(),
//     });
//     const byweekdayRule = new RRule({
//       ...options,
//       byweekday: getByWeekDay(dueDate),
//     });

//     return [bymonthdayRule, byweekdayRule];
//   }
// }, [dueDate, ruleOptions.freq, ruleOptions.interval]);



// this is not yet supported  PLEASE DO NOT DELETE
// const freqToRecurrenceUnits = (freq: Frequency) => {
//   const recUnitsSortedByFreqAsIndex: RecurrenceUnit[] = ["year", "month", "week", "day", "hour", "minute"]
//   return recUnitsSortedByFreqAsIndex[freq]
// }


// this is not yet supported  PLEASE DO NOT DELETE
// const handleclickSaveOrCancel = (e, type: string) => {
//   if (type === 'save') {

//     const getCount = () => { return endsOccurrences ?? undefined }

//     const rrule = new RRule({
//       freq: ruleOptions?.freq ?? undefined,
//       dtstart: new Date(schedule?.dueDate ?? undefined),
//       count: getCount(),
//       interval: ruleOptions?.interval ?? undefined,
//       byweekday: ruleOptions?.byweekday ?? undefined
//     })

//     setSchedule({
//       ...schedule,
//       isTimeValid: true,
//       isRecurring: ruleOptions?.interval && ruleOptions?.interval !== 0,
//       recurrenceQuantity: ruleOptions?.interval,
//       recurenceUnit: freqToRecurrenceUnits(ruleOptions?.freq),
//       cancelIfReceived: false,
//       rruleset: rrule.toString(),
//     })
//   }

//   handlePatternClick(e)
// }


// this is not yet supported  PLEASE DO NOT DELETE
// const saveOrCancel = () => {
//   return (
//     <Stack direction='row' sx={{
//       justifyContent: 'flex-end',
//       alignItems: 'center',
//       alignSelf: 'flex-end',
//       marginTop: '10px',
//     }}>
//       <BTButton
//         onClick={(e) => handleclickSaveOrCancel(e, "cancel")}
//         sx={{
//           endIcon: { margin: 0 },
//           marginRight: '10px',
//           padding: "8px",
//           width: "80px",
//           borderRadius: "8px",
//           background: "linear-gradient(44.54deg, #42d872 14.2%, #97e89c 85.23%)",
//         }}
//       >
//         <CancelOutlined style={{ marginInlineEnd: '5px', height: '14px', width: '14px' }} />
//         {"Cancel"}
//       </BTButton>
//       <BTButton
//         onClick={(e) => handleclickSaveOrCancel(e, "save")}
//         sx={{
//           endIcon: { margin: 0 },

//           padding: "8px",
//           width: "80px",
//           borderRadius: "8px",
//           background: "linear-gradient(44.54deg, #42d872 14.2%, #97e89c 85.23%)",
//         }}
//       >
//         <SaveOutlined style={{ marginInlineEnd: '5px', height: '14px', width: '14px' }} />
//         {"Save"}
//       </BTButton>
//     </Stack>
//   )
// }

// this is not yet supported  PLEASE DO NOT DELETE
// const patternPicker = () => {
//   const patternString = !!schedule?.rruleset ? RRule.fromString(schedule.rruleset).toText() : 'No pattern specified'
//   return (
//     <Box sx={{ display: 'flex' }}>
//       <>
//         <Button onClick={handlePatternClick} style={{ textTransform: 'capitalize' }}>
//           {patternString}
//         </Button>
//         <Popover open={Boolean(anchorElPattern)} anchorEl={anchorElPattern} onClose={handlePatternClick} >
//           <Stack direction='column' sx={{ m: '15px', }}>
//             <TimeAndDatePattern
//               ruleOptions={ruleOptions}
//               handleIntervalChange={handleIntervalChange}
//               handleFrequencyChange={handleFrequencyChange}
//               handleByweekdayChange={handleByweekdayChange}
//               selectedMonthlyRule={selectedMonthlyRule}
//               setSelectedMonthlyRule={setSelectedMonthlyRule}
//               monthlyRules={monthlyRules}
//               ends={ends}
//               setEnds={setEnds}
//               setEndsDate={setEndsDate}
//               endsDate={endsDate}
//               endsOccurrences={endsOccurrences}
//               setEndsOccurrences={setEndsOccurrences}
//             />
//             {saveOrCancel()}
//           </Stack>
//         </Popover>
//       </>

//     </Box>
//   )
// }

